<script lang="ts">
	import SvelteSeo from 'svelte-seo';
	import { page } from '$app/stores';
	import { onMount } from 'svelte';
	import { Button } from '$lib/components/ui/button';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import { Alert, AlertDescription } from '$lib/components/ui/alert';
	import { Card, CardContent, CardHeader, CardTitle } from '$lib/components/ui/card';
	import { Badge } from '$lib/components/ui/badge';
	import { CheckCircle, Sparkles, CloudUpload, Zap, Loader2, X, RefreshCw } from '@lucide/svelte';
	import { createPageSEO, createWebPageSchema } from '$lib/config/seo';
	import { useTest } from '$lib/components/tracking/useTest';
	import { cvUploadTest } from '$lib/components/tracking/ab-tests';
	import { fetchSSE } from '$lib/api';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';
	import { STAGE_LABELS } from '$lib/config/consts';
	import { PUBLIC_DASHBOARD_URL } from '$env/static/public';
	import { isDashboardAuthenticated } from '$lib/utils/auth-cookie';

	interface CVData {
		profile_name: string | null;
		email: string | null;
		job_title: string | null;
		location: string | null;
		description: string | null;
		total_experience: string | null;
		skills: string[];
		languages: Array<{ language: string; proficiency: string }>;
		experience: Array<{
			title: string;
			company: string;
			start_date: string;
			end_date: string;
			description: string;
			location: string;
		}>;
		education: Array<{
			degree: string;
			institution: string;
			start_date: string;
			end_date: string;
			description: string;
			location: string;
		}>;
		certifications: string[];
		links: Array<{ url: string; label: string }>;
	}

	interface DropSSEResult {
		success: boolean;
		type: 'cv' | 'jd' | 'unknown';
		saved: boolean;
		id?: string;
		token?: string;
		data?: CVData;
		missing_fields?: string[];
		confidence?: number;
		error?: string;
		updated?: boolean;
	}

	interface LanguageField {
		language: string;
		proficiency?: string;
	}

	interface ExperienceField {
		title: string;
		company: string;
	}

	interface EducationField {
		degree: string;
		institution: string;
	}

	interface LinkField {
		url: string;
		label?: string;
	}

	const test = useTest(cvUploadTest);
	const APP_URL = PUBLIC_DASHBOARD_URL || 'https://app.talentprofile.net';

	type Step = 'idle' | 'uploading' | 'missing-fields' | 'success' | 'error';
	let step = $state<Step>('idle');
	let message = $state('');
	let progress_stage = $state('');
	let progress_completed = $state(0);
	let progress_total = $state(0);
	let file_input_ref = $state<HTMLInputElement | null>(null);
	let drop_active = $state(false);
	let was_updated = $state(false);
	let is_app_user = $state(false);

	let parsed_data = $state<CVData | null>(null);
	let missing_fields = $state<string[]>([]);
	let partial_fields = $state<Record<string, unknown>>({});

	let email = $state('');
	let form_error = $state('');

	const CV_FIELD_LABELS: Record<string, string> = {
		profile_name: 'Name',
		email: 'Email',
		job_title: 'Job Title',
		location: 'Location',
		description: 'Summary',
		total_experience: 'Experience',
		skills: 'Skills',
		languages: 'Languages',
		experience: 'Work Experience',
		education: 'Education',
		certifications: 'Certifications',
		links: 'Links'
	};

	function format_field_value(value: unknown): string {
		if (value === null || value === undefined) return '';
		if (typeof value === 'string') return value;
		if (Array.isArray(value)) {
			if (value.length === 0) return '';
			const first = value[0];
			if (typeof first === 'string') return value.join(', ');
			if (typeof first === 'object' && first !== null) {
				if ('language' in first)
					return (value as LanguageField[]).map((l) => l.language).join(', ');
				if ('title' in first)
					return (value as ExperienceField[]).map((e) => `${e.title} at ${e.company}`).join(' • ');
				if ('degree' in first)
					return (value as EducationField[])
						.map((e) => `${e.degree} — ${e.institution}`)
						.join(' • ');
				if ('url' in first) return (value as LinkField[]).map((l) => l.url).join(', ');
			}
			return JSON.stringify(value);
		}
		return String(value);
	}

	const has_partial_data = $derived(Object.keys(partial_fields).length > 0);

	const partial_entries = $derived(
		Object.entries(partial_fields).filter(([, value]) => format_field_value(value) !== '')
	);

	const pending_keys = $derived(
		Object.keys(CV_FIELD_LABELS)
			.filter((k) => !(k in partial_fields))
			.slice(0, 3)
	);

	onMount(() => {
		test.event('page-loaded');
		is_app_user = isDashboardAuthenticated();
	});

	async function process_file(file: File) {
		if (file.size > 10 * 1024 * 1024) {
			step = 'error';
			message = 'File too large (max 10MB)';
			return;
		}

		test.event('file-selected', file.size);
		step = 'uploading';
		progress_stage = 'uploading';
		progress_completed = 0;
		progress_total = 4;
		partial_fields = {};
		test.event('upload-started');

		try {
			const form_data = new FormData();
			form_data.append('file', file);
			form_data.append('ab_test', test.id);
			form_data.append('ab_variant', test.variant);

			const result = await fetchSSE<DropSSEResult>(
				{
					url: `/drop`,
					method: 'POST',
					formData: form_data
				},
				{
					onProgress: (event) => {
						progress_stage = event.stage;
						if (event.completed !== undefined) progress_completed = event.completed;
						if (event.total !== undefined) progress_total = event.total;
					},
					onField: (key, value) => {
						partial_fields = { ...partial_fields, [key]: value };
					},
					onFieldReset: () => {
						partial_fields = {};
					}
				}
			);

			if (!result.success) {
				throw new Error(result.error || 'Failed to process file');
			}

			if (result.type !== 'cv') {
				step = 'error';
				message =
					'This document was not recognized as a CV/Resume. Please try uploading a CV, or use the Job Description page.';
				test.event('upload-failed');
				return;
			}

			test.event('upload-completed');
			test.event('parse-completed');
			was_updated = result.updated || false;

			if (result.saved && result.id && result.token) {
				step = 'success';
				message = was_updated
					? "Profile updated with your latest CV! We'll keep sending you matches."
					: "Profile saved! We'll send job matches to your email.";
				test.event('record-saved');
				test.goal(1);

				localStorage.setItem(
					'dropProfileData',
					JSON.stringify({
						type: 'cv',
						id: result.id,
						token: result.token
					})
				);
				window.dispatchEvent(new Event('auth-changed'));
			} else {
				parsed_data = result.data || null;
				missing_fields = result.missing_fields || [];
				step = 'missing-fields';
				test.event('form-viewed');
				if (parsed_data?.email) email = parsed_data.email;
			}
		} catch (e) {
			step = 'error';
			message = e instanceof Error ? e.message : 'Something went wrong';
			test.event('upload-failed');
		}
	}

	async function submit_data() {
		if (!parsed_data) return;

		test.event('form-submitted');
		step = 'uploading';
		progress_stage = 'saving';
		progress_completed = 0;
		progress_total = 0;
		partial_fields = {};

		try {
			const result = await fetchSSE<{
				success: boolean;
				id?: string;
				token?: string;
				error?: string;
				updated?: boolean;
			}>(
				{
					url: `/drop/complete`,
					method: 'POST',
					json: {
						type: 'cv',
						data: {
							...parsed_data,
							email: email || parsed_data.email
						},
						ab_test: test.id,
						ab_variant: test.variant
					}
				},
				{
					onProgress: (event) => {
						progress_stage = event.stage;
					}
				}
			);

			if (!result.success) {
				throw new Error(result.error || 'Failed to save');
			}

			test.event('record-saved');
			test.goal(1);
			was_updated = result.updated || false;

			if (result.id && result.token) {
				localStorage.setItem(
					'dropProfileData',
					JSON.stringify({
						type: 'cv',
						id: result.id,
						token: result.token
					})
				);
				window.dispatchEvent(new Event('auth-changed'));
			}

			step = 'success';
			message = was_updated
				? "Profile updated with your latest CV! We'll keep sending you matches."
				: "Profile saved! We'll send job matches to your email.";
		} catch (e) {
			step = 'error';
			message = e instanceof Error ? e.message : 'Failed to save';
		}
	}

	function handle_file_change(e: Event) {
		const file = (e.target as HTMLInputElement).files?.[0];
		if (file) process_file(file);
	}

	const ALLOWED_TYPES = new Set([
		'application/pdf',
		'application/msword',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		'text/plain'
	]);

	function handle_drop(e: DragEvent) {
		e.preventDefault();
		drop_active = false;
		const file = e.dataTransfer?.files?.[0];
		if (file && ALLOWED_TYPES.has(file.type)) {
			process_file(file);
		} else if (file) {
			step = 'error';
			message = 'Unsupported file type. Please use PDF, DOC, DOCX, or TXT.';
		}
	}

	function handle_dragover(e: DragEvent) {
		e.preventDefault();
		drop_active = true;
	}

	function handle_dragleave(e: DragEvent) {
		e.preventDefault();
		drop_active = false;
	}

	function handle_missing_fields_submit() {
		form_error = '';

		if (missing_fields.includes('email')) {
			if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
				form_error = 'Please enter a valid email';
				return;
			}
		}

		submit_data();
	}

	function reset() {
		step = 'idle';
		message = '';
		progress_stage = '';
		was_updated = false;
		partial_fields = {};
		if (file_input_ref) file_input_ref.value = '';
	}

	const seo = createPageSEO({
		title: 'Upload Your CV - Get Matched with Jobs in 24h | TalentProfile',
		description:
			'Upload your CV and get matched with qualified tech jobs within 24 hours. AI-powered matching means zero form filling. Free forever.',
		canonical: '/drop-cv',
		keywords:
			'upload CV, upload resume, job matching, AI job search, tech jobs, developer jobs, automatic job matching',
		ogImage: '/drop-cv.png',
		ogType: 'website',
		structuredData: createWebPageSchema({
			name: 'Upload CV',
			description: 'AI-powered CV upload for job matching',
			url: '/drop-cv'
		})
	});
</script>

{#key $page.url.pathname}
	<SvelteSeo {...seo} />
{/key}

<div class="flex min-h-[calc(100dvh-527px)] flex-col gap-4 bg-gradient-to-b from-white to-gray-50">
	<div class="container mx-auto px-4 py-16 md:py-20">
		{#if step === 'idle'}
			<div class="mx-auto max-w-7xl">
				<div class="grid items-center gap-12 sm:gap-24 md:gap-36 lg:grid-cols-2">
					<div class="space-y-6">
						<Badge class="mb-2" variant="secondary">
							<Sparkles class="mr-1 h-4 w-4" />
							Free · AI-Powered · Zero Forms
						</Badge>

						<h1 class="text-4xl leading-tight font-bold text-gray-900 md:text-6xl">
							Drop Your CV,<br />
							<span class="text-blue-600">Get Jobs</span><br />
							in 24 Hours
						</h1>

						<p class="text-xl leading-relaxed text-gray-600">
							No more filling endless application forms. Just drop your CV and our AI matches you
							with relevant tech jobs automatically.
						</p>

						<div class="flex flex-wrap items-center gap-6 pt-4 text-sm text-gray-600">
							<div class="flex items-center gap-2">
								<CheckCircle class="h-5 w-5 text-green-600" />
								<span>Zero form fields</span>
							</div>
							<div class="flex items-center gap-2">
								<CheckCircle class="h-5 w-5 text-green-600" />
								<span>AI extracts everything</span>
							</div>
							<div class="flex items-center gap-2">
								<CheckCircle class="h-5 w-5 text-green-600" />
								<span>Instant matching</span>
							</div>
						</div>

						<div class="space-y-3 pt-6 text-sm text-gray-600">
							<div class="flex items-start gap-3">
								<Zap class="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
								<div>
									<strong class="text-gray-900">AI Understands Your Experience:</strong> Extracts skills,
									years of experience, and tech stack automatically from your CV.
								</div>
							</div>
							<div class="flex items-start gap-3">
								<Zap class="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
								<div>
									<strong class="text-gray-900">Instant Matching:</strong> We start matching against thousands
									of jobs the moment you upload.
								</div>
							</div>
							<div class="flex items-start gap-3">
								<Zap class="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
								<div>
									<strong class="text-gray-900">Zero Maintenance:</strong> Drop it and forget it. We send
									you matches as they come in.
								</div>
							</div>
						</div>
					</div>

					<div class="relative">
						<Card
							class="border-2 bg-gradient-to-br {drop_active
								? 'border-blue-500 from-blue-100 to-indigo-100'
								: 'border-blue-200 from-blue-50 to-indigo-50'}"
						>
							<CardContent class="pt-6">
								<div
									class="space-y-6 py-8 text-center"
									role="region"
									aria-label="File drop zone"
									ondrop={handle_drop}
									ondragover={handle_dragover}
									ondragleave={handle_dragleave}
								>
									<div
										class="superellipse mx-auto flex h-20 w-20 items-center justify-center bg-blue-100"
										class:animate-pulse={!drop_active}
									>
										<CloudUpload class="h-10 w-10 text-blue-600" />
									</div>

									<div>
										<h2 class="mb-2 text-2xl font-bold text-gray-900">
											{drop_active ? 'Drop it!' : 'Drop Your CV Here'}
										</h2>
										<p class="text-gray-600">PDF, DOC, DOCX, or TXT · Max 10MB</p>
									</div>

									<input
										bind:this={file_input_ref}
										id="cv_file"
										type="file"
										accept=".pdf,.doc,.docx,.txt"
										class="hidden"
										onchange={handle_file_change}
									/>

									<Button size="lg" class="w-full" onclick={() => file_input_ref?.click()}>
										<Sparkles class="mr-2 h-5 w-5" />
										Choose File
									</Button>

									<div class="space-y-2 border-t pt-4 text-left text-sm text-gray-600">
										<div class="flex items-start gap-2">
											<CheckCircle class="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
											<span>AI extracts your skills automatically</span>
										</div>
										<div class="flex items-start gap-2">
											<CheckCircle class="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
											<span>Get matched with jobs within 24 hours</span>
										</div>
										<div class="flex items-start gap-2">
											<CheckCircle class="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
											<span>Never manually apply again</span>
										</div>
									</div>
								</div>
							</CardContent>
						</Card>

						<div
							class="absolute -top-4 -right-4 animate-bounce rounded-full bg-green-500 px-4 py-2 text-sm font-semibold text-white shadow-lg"
						>
							Free
						</div>
						<div
							class="absolute -bottom-4 -left-4 rounded-full bg-purple-500 px-4 py-2 text-sm font-semibold text-white shadow-lg"
						>
							Zero Forms
						</div>
					</div>
				</div>
			</div>
		{:else if step === 'uploading'}
			<div class="mx-auto max-w-md">
				<Card>
					<CardContent class="flex flex-col items-center py-8">
						{#if !has_partial_data}
							<Loader2 class="mb-4 h-12 w-12 animate-spin text-blue-600" />
						{/if}
						<p class="text-lg font-medium text-gray-900">
							{STAGE_LABELS[progress_stage] || 'Processing...'}
						</p>
						{#if progress_total > 0 && !has_partial_data}
							<div class="mt-6 w-full max-w-xs">
								<div class="h-2 w-full overflow-hidden rounded-full bg-gray-200">
									<div
										class="h-full rounded-full bg-blue-600 transition-all duration-500"
										style="width: {Math.min(
											100,
											Math.max(0, Math.round((progress_completed / progress_total) * 100))
										)}%"
									></div>
								</div>
								<p class="mt-2 text-center text-xs text-gray-500">
									Step {progress_completed} of {progress_total}
								</p>
							</div>
						{/if}

						{#if has_partial_data}
							<div class="mt-6 w-full space-y-2">
								{#each partial_entries as [key, value] (key)}
									<div
										class="animate-in fade-in slide-in-from-left-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
									>
										<p class="text-xs font-medium text-gray-500">
											{CV_FIELD_LABELS[key] || key}
										</p>
										<p class="line-clamp-2 text-sm text-gray-900">
											{format_field_value(value)}
										</p>
									</div>
								{/each}

								{#each pending_keys as pendingKey (pendingKey)}
									<div class="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
										<div class="h-3 w-16 animate-pulse rounded bg-gray-200"></div>
										<div class="mt-1 h-4 w-3/4 animate-pulse rounded bg-gray-200"></div>
									</div>
								{/each}
							</div>
						{/if}
					</CardContent>
				</Card>
			</div>
		{:else if step === 'missing-fields'}
			<div class="mx-auto max-w-md space-y-6">
				<Alert class="border-blue-200 bg-blue-50">
					<AlertDescription class="flex items-center gap-2 text-blue-800">
						<Sparkles class="h-5 w-5" />
						CV parsed! We just need your email to send you matches.
					</AlertDescription>
				</Alert>

				<Card>
					<CardHeader>
						<CardTitle>Almost There</CardTitle>
					</CardHeader>
					<CardContent class="space-y-4">
						<div class="space-y-2">
							<Label for="email">Email address *</Label>
							<p class="text-xs text-gray-500">We'll send job matches here</p>
							<Input
								id="email"
								type="email"
								placeholder="you@example.com"
								bind:value={email}
								class={form_error ? 'border-red-500' : ''}
							/>
						</div>

						{#if form_error}
							<p class="text-sm text-red-500">{form_error}</p>
						{/if}

						<Button class="w-full" onclick={handle_missing_fields_submit}>Get Job Matches</Button>
					</CardContent>
				</Card>
			</div>
		{:else if step === 'success'}
			<div class="mx-auto max-w-md">
				<Card class={was_updated ? 'border-blue-200 bg-blue-50' : 'border-green-200 bg-green-50'}>
					<CardContent class="flex flex-col items-center py-12 text-center">
						<div class="mb-4 rounded-full {was_updated ? 'bg-blue-100' : 'bg-green-100'} p-3">
							{#if was_updated}
								<RefreshCw class="h-10 w-10 text-blue-600" />
							{:else}
								<CheckCircle class="h-10 w-10 text-green-600" />
							{/if}
						</div>
						<h2 class="mb-2 text-2xl font-bold text-gray-900">
							{was_updated ? 'Updated!' : 'Success!'}
						</h2>
						<p class="text-gray-600">{message}</p>

						{#if is_app_user}
							<div class="mt-6 w-full space-y-3 border-t pt-4">
								<p class="text-sm text-gray-500">
									You already have an account. Your data will be linked automatically on your next
									sign-in.
								</p>
								<Button
									variant="outline"
									class="w-full"
									onclick={() => window.open(`${APP_URL}/talent/profile?claim_drop=1`, '_blank')}
								>
									Go to My Profile
								</Button>
							</div>
						{/if}
					</CardContent>
				</Card>
			</div>
		{:else if step === 'error'}
			<div class="mx-auto max-w-md">
				<Card class="border-red-200 bg-red-50">
					<CardContent class="flex flex-col items-center py-12 text-center">
						<div class="mb-4 rounded-full bg-red-100 p-3">
							<X class="h-10 w-10 text-red-600" />
						</div>
						<h2 class="mb-2 text-2xl font-bold text-gray-900">Something went wrong</h2>
						<p class="mb-4 text-gray-600">{message}</p>
						<Button variant="outline" onclick={reset}>Try Again</Button>
					</CardContent>
				</Card>
			</div>
		{/if}
	</div>

	{#if step === 'success'}
		<div class="container mx-auto px-4 py-8 md:pb-24">
			<GlopayCtaHorizontal placement="drop-cv-post-form" />
		</div>
	{/if}
</div>
